import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
    ArrowRight,
    ArrowsLeftRight,
    CircleNotch,
    Crosshair,
    Lightning,
    ListChecks,
    PencilSimple,
    ShieldWarning,
    Warning,
} from "@phosphor-icons/react";
import { fetchScanResult, trackEvent } from "../lib/api";

function ScoreCounter({ value }) {
    const [shown, setShown] = useState(0);
    useEffect(() => {
        if (value == null) return;
        let frame;
        const start = performance.now();
        const dur = 700;
        const loop = (t) => {
            const p = Math.min(1, (t - start) / dur);
            setShown(Math.round(value * p));
            if (p < 1) frame = requestAnimationFrame(loop);
        };
        frame = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(frame);
    }, [value]);
    return <span data-testid="result-overall-score" className="font-display">{shown}</span>;
}

function scoreColorClass(score) {
    if (score == null) return "text-white";
    if (score >= 80) return "text-biks-ok";
    if (score >= 65) return "text-yellow-300";
    if (score >= 50) return "text-orange-400";
    return "text-biks-danger";
}

const VERDICT_TONE = {
    "Strong structural consistency": "border-biks-ok/50 bg-biks-ok/10 text-biks-ok",
    "High score but exposed domain": "border-biks-warn/50 bg-biks-warn/10 text-biks-warn",
    "Workable but exposed": "border-biks-blueSoft/50 bg-biks-blue/10 text-biks-blueSoft",
    "Conflicting assumptions": "border-biks-warn/50 bg-biks-warn/10 text-biks-warn",
    "Structural contradiction": "border-biks-danger/50 bg-biks-danger/10 text-biks-danger",
    "Unsupported or internally inconsistent": "border-biks-danger/60 bg-biks-danger/15 text-biks-danger",
    "Underspecified": "border-biks-text3/60 bg-biks-surface/60 text-biks-text2",
};

function AnswerQuote({ item, testId, tone = "blue", stacked = false }) {
    const toneCls = tone === "amber" ? "text-biks-warn border-biks-warn/50" : "text-biks-blueSoft border-biks-blue/40";
    const layout = stacked ? "flex flex-col gap-1.5" : "flex flex-col gap-1.5 sm:flex-row sm:items-start sm:gap-4";
    return (
        <div data-testid={testId} className={layout}>
            <span className={`w-fit shrink-0 rounded border px-2 py-0.5 font-mono-data text-[11px] uppercase tracking-[0.12em] ${toneCls}`}>
                {item.answer}
            </span>
            <span className="text-sm text-biks-text2">
                <span className="text-white">&ldquo;{item.statement}&rdquo;</span>
                {item.domain && <span className="ml-2 text-[11px] uppercase tracking-[0.16em] text-biks-text3">{item.domain}</span>}
            </span>
        </div>
    );
}

function WeakestSignals({ signals }) {
    const groups = [
        ["disagreed", "Answers that count against the model", "These carry the diagnosis: structural claims you say are not true today, or a friction you confirmed.", "amber"],
        ["undecided", "You were undecided on", "Neutral answers are counted as weak. Deciding these is often faster than testing them.", "blue"],
        ["unknown", "You marked as 'not enough information'", "Unknowns are structural risk, not neutral ground, and each one lowers the score.", "blue"],
    ].filter(([k]) => signals?.[k]?.length);
    if (groups.length === 0) return null;
    return (
        <section data-testid="result-weakest-signals" className="border-b border-biks-line/50">
            <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-20">
                <div className="section-title mb-4 flex items-center gap-2">
                    <Crosshair size={14} weight="bold" />
                    Your weakest signals
                </div>
                <h3 className="font-display max-w-2xl text-2xl font-bold tracking-tight text-white sm:text-3xl">
                    The answers this diagnosis rests on.
                </h3>
                <p className="mt-4 max-w-2xl text-sm text-biks-text2">
                    BoM never saw your idea, only these answers. Everything above and below follows from them.
                </p>
                <div className="mt-10 grid gap-6 lg:grid-cols-3">
                    {groups.map(([key, title, hint, tone]) => (
                        <div key={key} data-testid={`result-weakest-${key}`} className="rounded-xl border border-biks-line/60 bg-biks-surface/40 p-6">
                            <div className="flex items-baseline justify-between gap-3">
                                <div className="text-sm font-semibold text-white">{title}</div>
                                <span className="font-mono-data text-xs text-biks-text3">{signals[key].length}</span>
                            </div>
                            <p className="mt-2 text-xs text-biks-text3">{hint}</p>
                            <div className="mt-5 space-y-4">
                                {signals[key].map((it) => (
                                    <AnswerQuote key={it.question_id} item={it} tone={tone} stacked testId={`result-weakest-${key}-${it.question_id}`} />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Contradictions({ items }) {
    if (!items || items.length === 0) return null;
    return (
        <section data-testid="result-contradictions" className="border-b border-biks-line/50">
            <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-20">
                <div className="section-title mb-4 flex items-center gap-2 !text-biks-danger">
                    <ArrowsLeftRight size={14} weight="bold" />
                    Contradictions in your own answers
                </div>
                <h3 className="font-display max-w-2xl text-2xl font-bold tracking-tight text-white sm:text-3xl">
                    Two things you said cannot both be true.
                </h3>
                <div className="mt-10 space-y-4">
                    {items.map((c, i) => (
                        <div key={c.statements.map((s) => s.question_id).join("-")} data-testid={`result-contradiction-${i}`} className="rounded-xl border border-biks-danger/40 bg-biks-danger/5 p-6 lg:p-8">
                            <div className="grid gap-4 lg:grid-cols-2">
                                {c.statements.map((st) => (
                                    <AnswerQuote key={st.question_id} item={st} tone="amber" testId={`result-contradiction-${i}-${st.question_id}`} />
                                ))}
                            </div>
                            <p className="mt-5 border-t border-biks-danger/30 pt-4 text-sm text-biks-text2">{c.explanation}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// Pentagon radar chart for the 5 domains.
// Wider-than-square viewBox so display_labels never clip at the edges.
function DomainRadar({ domains }) {
    const W = 480;
    const H = 380;
    const cx = W / 2;
    const cy = H / 2;
    const r = 120;
    const n = domains.length || 5;
    const ang = (i) => -Math.PI / 2 + (i * 2 * Math.PI) / n;

    const ringPoints = (k) =>
        domains
            .map((_, i) => {
                const x = cx + Math.cos(ang(i)) * r * k;
                const y = cy + Math.sin(ang(i)) * r * k;
                return `${x},${y}`;
            })
            .join(" ");

    const scorePoints = domains
        .map((d, i) => {
            const v = (d.score ?? 0) / 100;
            const x = cx + Math.cos(ang(i)) * r * v;
            const y = cy + Math.sin(ang(i)) * r * v;
            return `${x},${y}`;
        })
        .join(" ");

    const labelOf = (d) => d.display_label || d.label;

    return (
        <svg
            viewBox={`0 0 ${W} ${H}`}
            className="h-full w-full"
            data-testid="result-radar"
            preserveAspectRatio="xMidYMid meet"
        >
            {[0.25, 0.5, 0.75, 1].map((k) => (
                <polygon
                    key={k}
                    points={ringPoints(k)}
                    fill="none"
                    stroke="#1E3A8A"
                    strokeOpacity={k === 1 ? 0.9 : 0.45}
                    strokeWidth="1"
                />
            ))}
            {domains.map((d, i) => {
                const x = cx + Math.cos(ang(i)) * r;
                const y = cy + Math.sin(ang(i)) * r;
                return <line key={`axis-${d.id}`} x1={cx} y1={cy} x2={x} y2={y} stroke="#1E3A8A" strokeOpacity="0.4" />;
            })}
            <polygon
                points={scorePoints}
                fill="rgba(59,130,246,0.25)"
                stroke="#3B82F6"
                strokeWidth="2"
            />
            {domains.map((d, i) => {
                const a = ang(i);
                const rx = Math.cos(a);
                const ry = Math.sin(a);
                // Push labels further out and use anchor based on direction
                const x = cx + rx * (r + 30);
                const y = cy + ry * (r + 30);
                const anchor = rx > 0.2 ? "start" : rx < -0.2 ? "end" : "middle";
                const label = labelOf(d);
                const words = label.split(" ");
                // Two-line wrap if label has 2+ words
                const line1 = words.length > 1 ? words.slice(0, Math.ceil(words.length / 2)).join(" ") : label;
                const line2 = words.length > 1 ? words.slice(Math.ceil(words.length / 2)).join(" ") : "";
                return (
                    <g key={d.id}>
                        <text
                            x={x}
                            y={y - 6}
                            fill="#93B4D9"
                            fontSize="10"
                            textAnchor={anchor}
                            dominantBaseline="middle"
                            style={{ textTransform: "uppercase", letterSpacing: "0.16em", fontWeight: 600 }}
                        >
                            {line1}
                        </text>
                        {line2 && (
                            <text
                                x={x}
                                y={y + 6}
                                fill="#93B4D9"
                                fontSize="10"
                                textAnchor={anchor}
                                dominantBaseline="middle"
                                style={{ textTransform: "uppercase", letterSpacing: "0.16em", fontWeight: 600 }}
                            >
                                {line2}
                            </text>
                        )}
                        <text
                            x={x}
                            y={y + 20}
                            fill="#FFFFFF"
                            fontSize="11"
                            textAnchor={anchor}
                            dominantBaseline="middle"
                            fontFamily="JetBrains Mono"
                        >
                            {d.score == null ? "—" : d.score}
                        </text>
                    </g>
                );
            })}
        </svg>
    );
}

export default function Result() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        let active = true;
        fetchScanResult(id)
            .then((d) => { if (active) setData(d); })
            .catch((e) => {
                if (active) setError(e?.response?.data?.detail || "Could not load result.");
            });
        return () => { active = false; };
    }, [id]);

    if (error) {
        return (
            <main data-testid="result-error" className="flex min-h-[70vh] items-center justify-center px-6">
                <div className="max-w-md rounded-lg border border-biks-danger/50 bg-biks-danger/5 p-6">
                    <div className="flex items-center gap-2 text-biks-danger">
                        <Warning size={16} weight="bold" />
                        <span className="overline !text-biks-danger">Error</span>
                    </div>
                    <p className="mt-3 text-sm text-white">{error}</p>
                    <Link
                        to="/scan"
                        data-testid="result-error-restart"
                        className="mt-6 inline-flex items-center gap-2 rounded-md border border-biks-line px-4 py-2 text-sm hover:border-biks-blueSoft"
                    >
                        Restart diagnostic
                    </Link>
                </div>
            </main>
        );
    }

    if (!data) {
        return (
            <main data-testid="result-loading" className="flex min-h-[70vh] items-center justify-center">
                <div className="flex items-center gap-3 text-biks-text2">
                    <CircleNotch size={18} weight="bold" className="animate-spin text-biks-blue" />
                    Computing diagnostic...
                </div>
            </main>
        );
    }

    const verdictTone = data.verdict
        ? (VERDICT_TONE[data.verdict.label] || "border-biks-line bg-biks-surface text-white")
        : "border-biks-line bg-biks-surface text-white";

    return (
        <main data-testid="result-page" className="bg-biks-bg">
            {/* SCORE SUMMARY */}
            <section className="relative overflow-hidden border-b border-biks-line/50">
                <div aria-hidden className="bg-grid absolute inset-0 opacity-40" />
                <div
                    aria-hidden
                    className="absolute -right-32 top-0 h-[500px] w-[500px] rounded-full"
                    style={{ background: "radial-gradient(circle, rgba(59,130,246,0.18) 0%, rgba(59,130,246,0) 70%)" }}
                />
                <div className="relative mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-24">
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
                        <div className="section-title">Overall Feasibility Index (OFI)</div>
                        <div className="flex flex-wrap items-center gap-3">
                            {data.answers && Object.keys(data.answers).length > 0 && (
                                <button
                                    type="button"
                                    data-testid="result-edit-answers-button"
                                    onClick={() => navigate("/scan", { state: { prefillAnswers: data.answers, prefillContext: data.context } })}
                                    className="inline-flex items-center gap-2 rounded-md border border-biks-line px-4 py-2 text-xs uppercase tracking-[0.16em] text-biks-text2 transition-colors hover:border-biks-blueSoft hover:text-white"
                                >
                                    <PencilSimple size={12} weight="bold" />
                                    Edit my answers
                                </button>
                            )}
                            <Link
                                to={`/contact?ref=${data.id}`}
                                data-testid="result-top-review-link"
                                onClick={() => trackEvent("cta_click", { submissionId: data.id })}
                                className="inline-flex items-center gap-2 rounded-md bg-biks-blue px-5 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-white transition-colors hover:bg-blue-500"
                            >
                                Get a private review
                                <ArrowRight size={12} weight="bold" />
                            </Link>
                        </div>
                    </div>
                    <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
                        <div className="lg:col-span-7">
                            {data.core_score != null ? (
                                <>
                                    <div className="flex items-baseline gap-4">
                                        <span
                                            data-testid="result-score-number"
                                            className={`text-[6.5rem] font-extrabold leading-none sm:text-[8rem] lg:text-[10rem] ${scoreColorClass(data.core_score)}`}
                                        >
                                            <ScoreCounter value={data.core_score} />
                                        </span>
                                        <span className="font-mono-data text-2xl text-biks-text3">/100</span>
                                    </div>
                                    {data.verdict && (
                                        <div
                                            data-testid="result-verdict-badge"
                                            className={`mt-6 inline-block rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] ${verdictTone}`}
                                        >
                                            {data.verdict.label}
                                        </div>
                                    )}
                                    {data.verdict && (
                                        <p
                                            data-testid="result-verdict-summary"
                                            className="mt-6 max-w-2xl text-base text-biks-text2 sm:text-lg"
                                        >
                                            {data.verdict.summary}
                                        </p>
                                    )}
                                    {data.verdict?.drivers && (
                                        <p
                                            data-testid="result-verdict-drivers"
                                            className="mt-4 max-w-2xl font-mono-data text-xs leading-relaxed text-biks-blueSoft"
                                        >
                                            {data.verdict.drivers}
                                        </p>
                                    )}
                                    {data.reliability_fallback && (
                                        <div
                                            data-testid="result-reliability-warning"
                                            className="mt-6 flex max-w-2xl items-start gap-3 rounded-lg border border-biks-warn/50 bg-biks-warn/10 p-5"
                                        >
                                            <ShieldWarning size={18} weight="bold" className="mt-0.5 shrink-0 text-biks-warn" />
                                            <p className="text-sm leading-relaxed text-biks-warn">
                                                {data.reliability_fallback}
                                            </p>
                                        </div>
                                    )}
                                    {data.reliability_note && (
                                        <p
                                            data-testid="result-reliability-note"
                                            className="mt-4 max-w-2xl text-sm text-biks-warn"
                                        >
                                            {data.reliability_note}
                                        </p>
                                    )}
                                    {data.score_breakdown && data.score_breakdown.adjustments && data.score_breakdown.adjustments.length > 0 && (
                            <div data-testid="result-why-this-score" className="mt-8 max-w-2xl">
                                <div className="section-title mb-3">Why this score?</div>
                                <div className="rounded-lg border border-biks-line/70 bg-biks-surface/50 p-5">
                                    <dl className="space-y-3 text-sm text-biks-text2">
                                                <div className="flex items-baseline justify-between gap-4">
                                                    <dt>Domain average</dt>
                                                    <dd className="font-mono-data text-white">
                                                        {data.score_breakdown.base}
                                                    </dd>
                                                </div>
                                                {data.score_breakdown.adjustments.map((a, i) => (
                                                    <div key={a.label} data-testid={`result-score-adjustment-${i}`} className="flex items-start justify-between gap-4">
                                                        <div className="pr-4">
                                                            <dt className="text-white">{a.label}</dt>
                                                            {a.detail && <p className="mt-0.5 text-xs text-biks-text3">{a.detail}</p>}
                                                        </div>
                                                        <dd className={`shrink-0 font-mono-data ${a.delta < 0 ? "text-biks-warn" : "text-biks-ok"}`}>
                                                            {a.delta > 0 ? `+${a.delta}` : a.delta}
                                                        </dd>
                                                    </div>
                                                ))}
                                                <div className="flex items-baseline justify-between gap-4 border-t border-biks-line/40 pt-2">
                                                    <dt className="font-semibold text-white">Final OFI</dt>
                                                    <dd className="font-mono-data text-white">
                                                        {data.score_breakdown.final}
                                                    </dd>
                                                </div>
                                            </dl>
                                                </div>
                                            </div>
                                    )}
                                </>
                            ) : (
                                <div data-testid="result-reliability-fallback" className="max-w-2xl rounded-lg border border-biks-warn/40 bg-biks-warn/5 p-8">
                                    <div className="overline !text-biks-warn flex items-center gap-2">
                                        <ShieldWarning size={14} weight="bold" />
                                        Reliability fallback
                                    </div>
                                    <p className="mt-4 text-lg text-white">
                                        {data.reliability_fallback}
                                    </p>
                                    <p className="mt-3 text-sm text-biks-text2">
                                        Validation-oriented actions are still shown below.
                                    </p>
                                </div>
                            )}
                        </div>
                        <div className="lg:col-span-5">
                            <div className="section-title mb-4">Domain map</div>
                            <div className="rounded-xl border border-biks-line/70 bg-biks-surface/60 p-6 backdrop-blur">
                            <div className="w-full" style={{ aspectRatio: "480 / 380" }}>
                                <DomainRadar domains={data.domains} />
                            </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* PRIMARY KILL RISK */}
            {data.primary_kill_risk && (
                <section data-testid="result-primary-kill-risk" className="border-b border-biks-line/50">
                    <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-20">
                        <div className="section-title mb-4 flex items-center gap-2">
                            <Lightning size={14} weight="bold" />
                            Primary kill risk
                        </div>
                        <div className="rounded-xl border border-biks-blue/40 bg-gradient-to-br from-biks-blue/10 to-biks-surface/60 p-8 lg:p-12">
                            <h2 data-testid="result-primary-risk-name" className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
                                {data.primary_kill_risk.name}
                            </h2>
                            {data.primary_kill_risk.plain && (
                                <p data-testid="result-primary-risk-plain" className="mt-4 max-w-3xl text-base font-medium text-white sm:text-lg">
                                    <span className="mr-2 text-[11px] uppercase tracking-[0.18em] text-biks-blueSoft">In plain terms</span>
                                    {data.primary_kill_risk.plain}
                                </p>
                            )}
                            <p data-testid="result-primary-risk-copy" className="mt-6 max-w-3xl text-base text-biks-text2 sm:text-lg">
                                {data.primary_kill_risk.primary_copy}
                            </p>
                            {data.primary_kill_risk.contextual_interpretation && (
                                <p
                                    data-testid="result-primary-risk-context"
                                    className="mt-4 max-w-3xl border-l-2 border-biks-blueSoft pl-4 text-sm italic text-biks-blueSoft"
                                >
                                    {data.primary_kill_risk.contextual_interpretation}
                                </p>
                            )}
                        </div>

                        {data.secondary_kill_risk && (
                            <div data-testid="result-secondary-kill-risk" className="mt-10">
                                <div className="section-title mb-4 flex items-center gap-2">
                                    <Lightning size={12} weight="bold" />
                                    Secondary kill risk
                                </div>
                                <div className="rounded-xl border border-biks-warn/40 bg-gradient-to-br from-biks-warn/5 to-biks-surface/40 p-6 lg:p-8">
                                    <h3
                                        data-testid="result-secondary-risk-name"
                                        className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl"
                                    >
                                        {data.secondary_kill_risk.name}
                                    </h3>
                                    {data.secondary_kill_risk.plain && (
                                        <p data-testid="result-secondary-risk-plain" className="mt-3 max-w-3xl text-sm font-medium text-white sm:text-base">
                                            <span className="mr-2 text-[11px] uppercase tracking-[0.18em] text-biks-warn">In plain terms</span>
                                            {data.secondary_kill_risk.plain}
                                        </p>
                                    )}
                                    <p
                                        data-testid="result-secondary-risk-copy"
                                        className="mt-4 max-w-3xl text-sm text-biks-text2 sm:text-base"
                                    >
                                        {data.secondary_kill_risk.primary_copy}
                                    </p>
                                    {data.secondary_kill_risk.contextual_interpretation && (
                                        <p
                                            data-testid="result-secondary-risk-context"
                                            className="mt-3 max-w-3xl border-l-2 border-biks-warn/60 pl-4 text-sm italic text-biks-warn"
                                        >
                                            {data.secondary_kill_risk.contextual_interpretation}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            )}

            <Contradictions items={data.contradictions} />

            {/* CONSTRAINT MAP & SNAPSHOT */}
            <section data-testid="result-constraints-snapshot" className="border-b border-biks-line/50">
                <div className="mx-auto max-w-7xl px-6 py-16 lg:grid lg:grid-cols-12 lg:gap-10 lg:px-10 lg:py-20">
                    <div className="lg:col-span-7">
                        <div className="section-title mb-4">Constraints</div>
                        <h3 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                            Where the model breaks under pressure.
                        </h3>
                        <ol data-testid="result-constraint-list" className="mt-8 space-y-3">
                            {data.constraint_map.length === 0 && (
                                <li className="rounded-md border border-biks-line/60 bg-biks-surface/40 p-5 text-sm text-biks-text3">
                                    No structural constraints surfaced at this answer set.
                                </li>
                            )}
                            {data.constraint_map.map((c, i) => (
                                <li
                                    key={typeof c === "string" ? c : c.constraint}
                                    data-testid={`result-constraint-${i}`}
                                    className="flex gap-4 rounded-md border border-biks-line/60 bg-biks-surface/40 p-5"
                                >
                                    <span className="font-mono-data shrink-0 text-sm text-biks-blueSoft">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <div className="flex-1">
                                        <p className="text-sm text-white">
                                            {c.constraint || c}
                                        </p>
                                        {c.evidence && c.evidence.length > 0 ? (
                                            <div data-testid={`result-constraint-${i}-evidence`} className="mt-3 space-y-2 border-t border-biks-line/40 pt-3">
                                                <div className="text-[11px] uppercase tracking-[0.18em] text-biks-blueSoft">Your answer{c.evidence.length > 1 ? "s" : ""}</div>
                                                {c.evidence.map((e) => (
                                                    <AnswerQuote key={e.question_id} item={e} testId={`result-constraint-${i}-evidence-${e.question_id}`} />
                                                ))}
                                            </div>
                                        ) : c.signal && (
                                            <p
                                                data-testid={`result-constraint-${i}-signal`}
                                                className="mt-2 text-[11px] uppercase tracking-[0.18em] text-biks-text3"
                                            >
                                                <span className="text-biks-blueSoft">Signal:</span>{" "}
                                                <span className="normal-case tracking-normal text-biks-text2">
                                                    {c.signal}
                                                </span>
                                            </p>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </div>
                    <div className="mt-12 lg:col-span-5 lg:mt-0">
                        <div className="section-title mb-4">Evidence, bias & context</div>
                        <div data-testid="result-snapshot" className="rounded-xl border border-biks-line/70 bg-biks-surface/40 p-6">
                            <dl className="space-y-4">
                                {data.snapshot.map((s) => (
                                    <div key={s.label} className="border-b border-biks-line/40 pb-3 last:border-0 last:pb-0">
                                        <dt className="section-subtitle mb-1.5">{s.label}</dt>
                                        <dd className="text-sm text-white">{s.value}</dd>
                                    </div>
                                ))}
                            </dl>
                        </div>
                    </div>
                </div>
            </section>

            <WeakestSignals signals={data.weakest_signals} />

            {/* THREE ACTIONS */}
            <section data-testid="result-actions-section" className="border-b border-biks-line/50">
                <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-20">
                    <div className="section-title mb-4 flex items-center gap-2">
                        <ListChecks size={14} weight="bold" />
                        Three concrete actions
                    </div>
                    <h2 className="font-display max-w-2xl text-2xl font-bold tracking-tight text-white sm:text-3xl">
                        Do these next.
                    </h2>
                    <ol data-testid="result-actions-list" className="mt-10 grid grid-cols-1 gap-4 lg:grid-cols-3">
                        {data.actions.length === 0 && (
                            <li className="rounded-lg border border-biks-line/60 bg-biks-surface/40 p-6 text-sm text-biks-text2">
                                No actions surfaced.
                            </li>
                        )}
                        {data.actions.map((a, i) => {
                            const text = typeof a === "string" ? a : a.text;
                            const why = typeof a === "string" ? null : a.why;
                            const doneWhen = typeof a === "string" ? null : a.done_when;
                            return (
                                <li
                                    key={typeof a === "string" ? a : a.text}
                                    data-testid={`result-action-${i}`}
                                    className="rounded-lg border border-biks-line/60 bg-biks-surface/40 p-6 transition-colors hover:border-biks-blue/60"
                                >
                                    <div className="font-mono-data text-sm text-biks-blueSoft">
                                        {String(i + 1).padStart(2, "0")}
                                    </div>
                                    <p className="mt-4 text-sm text-white">{text}</p>
                                    {doneWhen && (
                                        <p
                                            data-testid={`result-action-${i}-done-when`}
                                            className="mt-3 rounded-md border border-biks-blue/30 bg-biks-blue/10 p-3 text-xs text-biks-blueSoft"
                                        >
                                            <span className="font-semibold uppercase tracking-[0.14em]">
                                                Done when:
                                            </span>{" "}
                                            <span className="text-biks-text2">{doneWhen}</span>
                                        </p>
                                    )}
                                    {why && (
                                        <p
                                            data-testid={`result-action-${i}-why`}
                                            className="mt-3 border-t border-biks-line/40 pt-3 text-[11px] uppercase tracking-[0.18em] text-biks-text3"
                                        >
                                            <span className="text-biks-blueSoft">
                                                Why this action:
                                            </span>{" "}
                                            <span className="normal-case tracking-normal text-biks-text2">
                                                {why}
                                            </span>
                                        </p>
                                    )}
                                </li>
                            );
                        })}
                    </ol>
                </div>
            </section>

            {/* CTA */}
            <section id="cta" data-testid="result-cta" className="border-b border-biks-line/50 bg-biks-bg">
                <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-24">
                    <div className="section-title mb-4">Private review</div>
                    <div className="overflow-hidden rounded-xl border border-biks-line/70 bg-gradient-to-br from-biks-surface/80 to-biks-surface/30 p-10 lg:p-16">
                        <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
                            <div className="lg:col-span-8">
                                <h2 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
                                    Request a Private Idea &amp; Strategy Review
                                </h2>
                                <p data-testid="result-cta-text-1" className="mt-6 max-w-2xl text-base text-biks-text2">
                                    BoM can stress-test the structure behind an
                                    idea without needing to know the idea
                                    itself. A private review takes it one step
                                    further by looking at your BoM result in
                                    light of the idea and the context around it.
                                </p>
                                <p data-testid="result-cta-text-2" className="mt-4 max-w-2xl text-base text-biks-text2">
                                    Share as much or as little of your idea as
                                    you want. I'll look at where the specifics
                                    strengthen or weaken the result, what BoM
                                    may not have captured and where I'd be most
                                    cautious before you spend more time or
                                    money.
                                </p>
                                <p data-testid="result-cta-price" className="mt-6 font-mono-data text-lg text-white">
                                    $120 · 60 minutes · one-on-one video call
                                </p>
                            </div>
                            <div className="lg:col-span-4 lg:text-right">
                                <Link
                                    to={`/contact?ref=${data.id}`}
                                    data-testid="result-contact-link"
                                    onClick={() => trackEvent("cta_click", { submissionId: data.id })}
                                    className="inline-flex items-center gap-3 rounded-md bg-biks-blue px-7 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-colors hover:bg-blue-500"
                                >
                                    Request a Private Review ($120)
                                    <ArrowRight size={14} weight="bold" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* DISCLAIMER */}
            <section data-testid="result-disclaimer" className="bg-biks-bg">
                <div className="mx-auto max-w-3xl px-6 py-12 text-center text-xs text-biks-text3 lg:px-10">
                    {data.disclaimer}
                </div>
            </section>
        </main>
    );
}
